import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from '../services/global.service';
import { ModalService } from '../shared/modal/modal.service';
import { SnackBarService } from '../shared/snackbar/snackbar.service';
import { LoadingService } from '../shared/loading/loading.service';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import Utils from '../shared/utility';

import * as shape from 'd3-shape';
import { formatCurrency } from '@angular/common';

@Component({
  selector: 'app-social-security',
  templateUrl: './social-security.component.html',
  styleUrls: ['./social-security.component.css']
})
export class SocialSecurityComponent implements OnInit {

  @ViewChild('illustrationDD') illustrationDD: ElementRef;

  public error: string;

  public client: any = {};
  public ssInputs: any = {};
  public ssData: any;

  public earliestVsAlternate: any;
  public comparisonGraph: any;

  public illustrationComparisons: any[];
  public selectedIllustration: any;
  public comparisonIllustration: any;

  public discountRate: number = 3;

  public clientMonthlyBenefitAmount: number = 0;
  public spouseMonthlyBenefitAmount: number = 0;

  public startPIA;
  public endPIA;
  public spouseStartPIA;
  public spouseEndPIA; 

  public alternateMessage: string = "This claim age is based on the user-inputted desired claim age above, and may be specific to the month of filing. If a number of months is listed after the year of age, the filing age is that many months past the beneficiary’s birthday. For example, “68 and 4 months” would be four months after the month in which they turn 68.";
  public switchSurvivorMessage: string = `At Age 67, you will begin collecting survivor benefits based on your late spouse's earnings record. At age 70, you will switch to retirement benefits based on your own earnings record. Got questions? Check out the instructional video in the Info Center under "Training Video Series", call our support line at 800-969-6518, or click on the Support icon in the bottom-right of your screen to create a support ticket.`;
  public futureCostMessage: string = 'Selecting "Todays Dollars" will show benefits in the net present value, assuming an annual inflation rate based on the amount selected to the right. Selecting "Future Dollars" will show the actual projected benefit in the given year.';
  public inflationRateMessage: string = "Enter the annual inflation rate being applied to projected future benefits when run in Today’s Dollars.";
  public colaMessage: string = "Cost-of-Living Adjustment (COLA) is the percentage change in benefits from year-to-year by the Social Security Administration. These adjustments are made to account for inflation on an annual basis. The default rates are consistent with the latest Social Security Trustee projections, but may be changed.";
  public primaryInsuranceAmountMessage: string = "Primary Insurance Amount is the monthly benefit the client will receive from Social Security at Full Retirement Age based on their earnings record. This amount is needed to project benefits throughout the client’s lifetime. This amount can be found at <a target='blank' href='https://secure.ssa.gov/RIL/SiView.do'>https://secure.ssa.gov/RIL/SiView.do</a>.";
  public primaryInsuranceAmountSurvivorMessage: string = "This is the year at which the survivor benefit is initially collected. Benefits collected before Full Retirement Age may be reduced from the late spouse’s PIA, and will be subject to the Earnings Test for the survivor.";
  
  public claimStatusMessage: string = "If a client is already receiving Social Security benefits, selecting “yes” will appropriately restrict the strategies the output will suggest.";
  public claimAgeMessage: string = "You can adjust the claiming age to instantly show the results associated with any filing age. Enter in the age (by year and month) when claiming is desired. For someone considering filing three months after their 65th birthday, enter 65 for the “Year” and 3 for the “Month”. NOTE: Your claim age is adjusted to fit into Social Security filing age restrictions.";
  public cashFlowMessage: string = "This illustration shows expected Social Security benefits earned year-by-year. Note that the projections are shown for each spouse (if married) and that the “Net of Medicare” column shows totals after Medicare Part B premiums and any potential Medicare surcharges are deducted from benefits.";

  public medicareDeductionsInfo: string = "This is the amount that is withheld from gross Social Security benefits and automatically paid to Medicare. Amount will include Medicare Part B premiums, and may include Part B and D surcharges, if applicable.";
  public netOfMedicareInfo: string = "Net benefits after Medicare deductions are subtracted";

  public breakEvenMessage: string = "The break-even point (highlighted here in orange) is the age when the total Social Security income between retirement options becomes equivalent. If your longevity is in question, or you don’t have an immediate need for retirement benefits before FRA, you may want to make your choice based on what will generate the largest lifetime income within your broader retirement plan.";
  public strategyComparisonMessage: string = "Directly compare two of the three strategies with this drop-down menu. Remember that in this illustration, as well as the entire application and report, Earliest is in red, Alternate is in blue, and Optimal is in green.";

  public survivorSwitchMessage: string = "The year in which the client switches from collecting survivor benefits to their own retirement benefit. If the switch age is past their FRA. they will receive a benefit larger than their PIA.";

  public survivorSwitchInfo: string = `<b>Switch to Retirement Benefit Age</b>
    <br /><br/>
    Those who have outlived a spouse (or qualifying ex-spouse) may be eligible for survivor benefits from Social Security as an alternative to their own retirement benefit. Survivors may begin collecting a survivor benefit as early as age 60 and subsequently change to their personal retirement benefit as late as age 70. Delaying the collection of this benefit can result in a higher monthly check.
    <br/>  <br/>
    Please keep in mind:
    <br /><br/>
    <b>•	Benefit amounts are affected by the timing of claims</b>
      <br/><br/>&nbsp&nbsp- Claiming <i>before</i> one’s FRA may reduce monthly benefits
      <br/><br/>&nbsp&nbsp- Claiming <i>after</i> one’s FRA may increase potential benefits
    <br /><br/>
    <b>•	In some cases, delaying a switch from survivor to personal benefits may not always be an improvement</b>
      <br/><br/>&nbsp&nbsp- If a client’s retirement  PIA is significantly smaller than that of their late spouse, the survivor benefit may always be the greater benefit
      <br/><br/>&nbsp&nbsp- Alternatively, someone with a relatively small survivor benefit may not wish to experience small Social Security benefits while waiting for their personal benefit to grow
    <br /><br/>
    <b>•	It may take time for a personal benefit to grow larger than a survivor benefit</b>
      <br/><br/>&nbsp&nbsp- For example, collecting survivor benefits at 67 and switching to personal benefits at 69 (two years of delayed retirement credits for collecting after FRA) may not be enough to boost the client’s PIA to an amount higher than the survivor benefit. However, collecting survivor benefits at 67 and switching at 70 (three years of delayed retirement credits) may provide enough of an increase to make the switch effective.
    <br /><br/>
    Got questions? Call our support line at 800-969-6518, or click on the Support icon in the bottom-right of your screen to create a support ticket.
    `;

  private previousSurvivorYears = 70;
  private previousClaimYears = 67;
  public clientSurvivorSwitchYears: number = 70;
  public clientSurvivorSwitchMonths: number = 0;

  curves = {
    Basis: shape.curveBasis,
    'Basis Closed': shape.curveBasisClosed,
    Bundle: shape.curveBundle.beta(1),
    Cardinal: shape.curveCardinal,
    'Cardinal Closed': shape.curveCardinalClosed,
    'Catmull Rom': shape.curveCatmullRom,
    'Catmull Rom Closed': shape.curveCatmullRomClosed,
    Linear: shape.curveLinear,
    'Linear Closed': shape.curveLinearClosed,
    'Monotone X': shape.curveMonotoneX,
    'Monotone Y': shape.curveMonotoneY,
    Natural: shape.curveNatural,
    Step: shape.curveStep,
    'Step After': shape.curveStepAfter,
    'Step Before': shape.curveStepBefore,
    default: shape.curveLinear
  };

  curveType: string = 'Step';
  curve: any = this.curves[this.curveType];
  interpolationTypes = [
    'Basis', 'Bundle', 'Cardinal', 'Catmull Rom', 'Linear', 'Monotone X',
    'Monotone Y', 'Natural', 'Step', 'Step After', 'Step Before'
  ];

  closedCurveType: string = 'Linear Closed';
  closedCurve: any = this.curves[this.closedCurveType];
  closedInterpolationTypes = [
    'Basis Closed', 'Cardinal Closed', 'Catmull Rom Closed', 'Linear Closed'
  ];

  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = false;
  showXAxisLabel = true;
  // xAxisLabel = 'Monthly Income';
  showYAxisLabel = true;
  yAxisLabel = 'Monthly Income';

  colorScheme = {
    domain: ['#bac7ad', '#aebcc7', '#c6989b']
  };
  
  // line, area
  autoScale = true;

  public illustrationsTypes = ["Optimal", "Alternate", "Earliest"];
  public includeSpouse = false;

  public colas: any[] = [
    { Cola: 0, Year: 0},
    { Cola: 0, Year: 0},
    { Cola: 0, Year: 0},
    { Cola: 0, Year: 0},
  ];

  public clientPIA: Number = 1250;
  public clientClaimStatus: string = "False";
  public clientRetirementYears: Number = 67;
  public clientRetirementMonths: Number = 0;
  public spousePIA: Number = 1250;
  public spouseClaimStatus: string = "False";
  public spouseRetirementYears: Number = 67;
  public spouseRetirementMonths: Number = 0;
  public presentValue: string = "True";

  public previousSpouse: any;

  public clientClaimStatusDisabled: boolean = false;
  public spouseClaimStatusDisabled: boolean = false;

  public modalSubscription;

  constructor(private globalService: GlobalService, private modalService: ModalService, private snackBarService: SnackBarService, private router: Router, private loadingService: LoadingService, private sanitizer: DomSanitizer) {
    this.modalSubscription = modalService.modal$.subscribe((modal) => {
      if (modal.modalName == 'add-edit-clientClosed') { // TODO: Fix this hack -- naming it hidden.. modals in general need to be refactored
        this.getClient();
      }
    });

    const yearlyColas = JSON.parse(localStorage.getItem('yearlyValues')) || { BaseYear: 2020, Year1: 1.3, Year2: 2.6, Year3: 2.6, Year4: 2.6 };
    this.colas[0] = { Year: yearlyColas.BaseYear, Cola : yearlyColas.Year1 };
    this.colas[1] = { Year: yearlyColas.BaseYear + 1, Cola : yearlyColas.Year2 };
    this.colas[2] = { Year: yearlyColas.BaseYear + 2, Cola : yearlyColas.Year3 };
    this.colas[3] = { Year: yearlyColas.BaseYear + 3, Cola : yearlyColas.Year4 };

    this.getClient();
  }

  ngOnDestroy() {
    this.modalSubscription.unsubscribe();
  }

  ngOnInit() {
  }

  colaUpdate(e, i) {
    this.colas[i].Cola = Number(e.target.value);
    this.inputFocusOut(e.target);
  }

  clientMonthlyBenefitUpdate(e) {
    this.inputFocusOut(e.target);
  }

  discountRateUpdate(e) {
    this.discountRate = e.target.value;
    this.inputFocusOut(e.target);
  }

  clientPIAUpdate(e) {
    this.clientPIA = Number(e.target.value);
    this.inputFocusOut(e.target);
  }

  clientClaimStatusUpdate(e) {
    // this.clientClaimStatus = e.target.val;
    // if (this.clientClaimStatus == 'No')
    //   this.clientClaimStatus = 'False';
    this.inputFocusOut(e.target);
  }

  clientRetirementYearsUpdate(e) {
    this.switchSurvivorMessage = this.switchSurvivorMessage.replace("At Age " + this.previousClaimYears.toString() + ", you will begin collecting", "At Age " + this.clientRetirementYears.toString() + ", you will begin collecting");
    this.previousClaimYears = Number(this.clientRetirementYears);
    this.ssRetirementYearFocusOut(e, this.client.clientProfile, true);
  }

  spouseRetirementYearsUpdate(e) {
    this.ssRetirementYearFocusOut(e, this.client.clientProfile.Spouses[0], false);
  }

  private typingTimer1;
  private doneTypingInterval1 = 500;
  private typerWaiting1 = false;

  ssRetirementYearFocusOut(e, person, client) {
    clearTimeout(this.typingTimer1);
    if (e.target.value) {
      this.typingTimer1 = setTimeout(() => {

        if (this.client.clientProfile.Spouses[0] && this.client.clientProfile.Spouses[0].maritalStatus == "Widowed") {
          if (e.target.value < 62) {
            this.clientRetirementYears = 62;
            e.target.value = 62;
          } else {
            this.clientRetirementYears = 70;
            e.target.value = 70;
          }
          
          if (e.target.value >= 60 && e.target.value <= 70) {
            this.clientRetirementYears = Number(e.target.value);
            this.getSSData();
          }
        } else {
          if (client) {
            if (e.target.value >= 60 && e.target.value <= 70 && this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == "Widowed") {
              this.clientRetirementYears = Number(e.target.value);
              this.getSSData();
            } else if (e.target.value >= 62 && e.target.value <= 70) {
              this.clientRetirementYears = Number(e.target.value);
              this.getSSData();
            } else {
              if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus != "Widowed") {
                if (e.target.value < 62) {
                  this.clientRetirementYears = 62;
                  e.target.value = 62;
                } else {
                  this.clientRetirementYears = 70;
                  e.target.value = 70;
                }
              } else {
                if (e.target.value < 60) {
                  this.clientRetirementYears = 60;
                  e.target.value = 60;
                } else {
                  this.clientRetirementYears = 70;
                  e.target.value = 70;
                }
              }
              this.getSSData();
            }
          } else {
            if (e.target.value >= 60 && e.target.value <= 70 && this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == "Widowed") {
              this.spouseRetirementYears = Number(e.target.value) || 67;
              this.inputFocusOut(e.target);
            } else if (e.target.value >= 62 && e.target.value <= 70) {
              this.spouseRetirementYears = Number(e.target.value) || 67;
              this.inputFocusOut(e.target);
            } else {
              if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus != "Widowed") {
                if (e.target.value < 62) {
                  this.spouseRetirementYears = 62;
                  e.target.value = 62;
                } else {
                  this.spouseRetirementYears = 70;
                  e.target.value = 70;
                }
              } else {
                if (e.target.value < 60) {
                  this.spouseRetirementYears = 60;
                  e.target.value = 60;
                } else {
                  this.spouseRetirementYears = 70;
                  e.target.value = 70;
                }
              }
              this.getSSData();
            }
          }
        }
      }, this.doneTypingInterval1);
    }
  }

  private typingTimer2;
  private doneTypingInterval2 = 500;
  private typerWaiting2 = false;

  ssSurvivorYearFocusOut(e, person, client) {
    clearTimeout(this.typingTimer2);
    if (e.target.value) {
      this.typingTimer2 = setTimeout(() => {

        if (this.client.clientProfile.Spouses[0] && this.client.clientProfile.Spouses[0].maritalStatus == "Widowed") {
          if (e.target.value < 62) {
            this.clientSurvivorSwitchYears = 62;
            e.target.value = 62;
          } else {
            this.clientSurvivorSwitchYears = 70;
            e.target.value = 70;
          }
          
          if (e.target.value >= 60 && e.target.value <= 70) {
            this.clientSurvivorSwitchYears = Number(e.target.value);
            this.getSSData();
          }
        } else {
          if (client) {
            if (e.target.value >= 60 && e.target.value <= 70 && this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == "Widowed") {
              this.clientSurvivorSwitchYears = Number(e.target.value);
              this.getSSData();
            } else if (e.target.value >= 62 && e.target.value <= 70) {
              this.clientSurvivorSwitchYears = Number(e.target.value);
              this.getSSData();
            } else {
              if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus != "Widowed") {
                if (e.target.value < 62) {
                  this.clientSurvivorSwitchYears = 62;
                  e.target.value = 62;
                } else {
                  this.clientSurvivorSwitchYears = 70;
                  e.target.value = 70;
                }
              } else {
                if (e.target.value < 60) {
                  this.clientSurvivorSwitchYears = 60;
                  e.target.value = 60;
                } else {
                  this.clientSurvivorSwitchYears = 70;
                  e.target.value = 70;
                }
              }
              this.getSSData();
            }
          } else {
            if (e.target.value >= 60 && e.target.value <= 70 && this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == "Widowed") {
              this.spouseRetirementYears = Number(e.target.value) || 67;
              this.inputFocusOut(e.target);
            } else if (e.target.value >= 62 && e.target.value <= 70) {
              this.spouseRetirementYears = Number(e.target.value) || 67;
              this.inputFocusOut(e.target);
            } else {
              if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus != "Widowed") {
                if (e.target.value < 62) {
                  this.spouseRetirementYears = 62;
                  e.target.value = 62;
                } else {
                  this.spouseRetirementYears = 70;
                  e.target.value = 70;
                }
              } else {
                if (e.target.value < 60) {
                  this.spouseRetirementYears = 60;
                  e.target.value = 60;
                } else {
                  this.spouseRetirementYears = 70;
                  e.target.value = 70;
                }
              }
              this.getSSData();
            }
          }
        }
      }, this.doneTypingInterval2);
    }
  }

  clientRetirementMonthsUpdate(e) {
    if (e.target.value.toString() == 0) {
      this.switchSurvivorMessage = this.switchSurvivorMessage.replace("At Age " + this.clientRetirementYears.toString() + " and " + this.clientRetirementMonths.toString() + ", you will begin collecting", "At Age " + this.clientRetirementYears.toString() + ", you will begin collecting");
    } else {
      this.switchSurvivorMessage = this.switchSurvivorMessage.replace("At Age " + this.clientRetirementYears.toString() + ", you will begin collecting", "At Age " + this.clientRetirementYears.toString() + " and " + e.target.value.toString() + ", you will begin collecting");
      this.switchSurvivorMessage = this.switchSurvivorMessage.replace("At Age " + this.clientRetirementYears.toString() + " and " + this.clientRetirementMonths.toString() + ", you will begin collecting", "At Age " + this.clientRetirementYears.toString() + " and " + e.target.value.toString() + ", you will begin collecting");
    }
    
    this.clientRetirementMonths = Number(e.target.value);

    if (typeof this.clientRetirementMonths == 'number' && !isNaN(this.clientRetirementMonths)) {
      if (this.clientRetirementMonths < 0) {
        this.clientRetirementMonths = 0;
      } else if (this.clientRetirementMonths >= 12) {
        this.clientRetirementMonths = 11;
      }
    } else {
      this.clientRetirementMonths = 0;
    }
    
    this.inputFocusOut(e.target);
  }

  clientSurvivorSwitchYearsUpdate(e) {
    this.switchSurvivorMessage = this.switchSurvivorMessage.replace(" record. At age " + this.previousSurvivorYears.toString(), " record. At age " + this.clientSurvivorSwitchYears.toString());
    this.previousSurvivorYears = this.clientSurvivorSwitchYears;
    // this.inputFocusOut(e.target);
    this.ssSurvivorYearFocusOut(e, this.client.clientProfile.Spouses[0], false);
  }

  clientSurvivorSwitchMonthsUpdate(e) {

    this.inputFocusOut(e.target);
  }

  spousePIAUpdate(e) {
    this.spousePIA = Number(e.target.value);
    this.inputFocusOut(e.target);
  }

  spouseClaimStatusUpdate(e) {
    // this.spouseClaimStatus = e.target.val;
    // if (this.spouseClaimStatus == 'No')
    //   this.spouseClaimStatus = 'False';
    this.inputFocusOut(e.target);
  }

  

  spouseRetirementMonthsUpdate(e) {
    this.spouseRetirementMonths = Number(e.target.value);

    if (typeof this.spouseRetirementMonths == 'number' && !isNaN(this.spouseRetirementMonths)) {
      if (this.spouseRetirementMonths < 0) {
        this.spouseRetirementMonths = 0;
      } else if (this.spouseRetirementMonths >= 12) {
        this.spouseRetirementMonths = 11;
      }
    } else {
      this.spouseRetirementMonths = 0;
    }
    this.inputFocusOut(e.target);
  }

  presentValueUpdate(e) {
    this.presentValue = e.target.value;
    this.inputFocusOut(e.target);
  }

  private typingTimer;
  private doneTypingInterval = 2000;
  private typerWaiting = false;

  inputFocusOut(el) {
    clearTimeout(this.typingTimer);
    if (el.value) {
      this.typingTimer = setTimeout(() => {
        this.getSSData();
      }, this.doneTypingInterval);
    }
  }

  public earliestIllTotals: any = {};
  public alternateIllTotals: any = {};
  public optimalIllTotals: any = {};

  getSSData() {
    
    this.loadingService.isLoading(true);
    var tempChildren = [];
    var tempChild = {};

    
    if (this.client.clientProfile.Children && this.client.clientProfile.Children.length) {
      for (var k in this.client.clientProfile.Children) {
        tempChild = {};
        tempChild["CurrentAge"] =  Number(this.client.clientProfile.Children[k].DependentChildAge);//this.getNumber(this.editClientModel.clientProfile.Spouses[k].currentIncome);
        tempChild['isDisabled'] = false;
        tempChild['IsStudentAt19'] = false; //this.client.clientProfile.Children[k].DependentChildStudent == "Yes" ? true : false;
        tempChildren.push(tempChild);
      }
    }

    var ssObject = {

      //HACK: used for backwards compatibility with redesign
      FrmPhase1Rate: 6,
      FrmPhase1Periods: Math.max(65 - Number(this.calculateAge(this.client.client.DOB))),
      FrmPhase2Rate: 6,
      FrmPhase2Periods: 10,
      FrmPhase3Rate: 6,

      FrmName0: this.client.client.FirstName + " " + this.client.client.LastName,
      SelfCurrentMonthlyBenefit: this.clientMonthlyBenefitAmount,
      SpouseAlreadyCollecting: this.spouseClaimStatus, //'False',//this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.spouseClaimStatus : "False",
      SelfGovPensionAmount: this.client.clientProfile.Pension,// "", // --> Needs to be updated, but how is this calculated?
      Children: this.client.clientProfile.Children && this.client.clientProfile.Children.length > 0 ? tempChildren : [],
      SelfAlreadyCollecting: this.clientClaimStatus, //'False',//this.clientClaimStatus,
      // SpouseLifeExpectancy: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? 89 : 0, // make this part of ssData output
      selfConditions: JSON.stringify({
          HCHBP: this.client.clientProfile.HCHBP,
          HCHC: this.client.clientProfile.HCHC,
          HCT2D: this.client.clientProfile.HCT2D,
          HCCD: this.client.clientProfile.HCCD,
          HCC: this.client.clientProfile.HCC,
          HCT: this.client.clientProfile.HCT,
      }),
      spouseConditions: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? JSON.stringify({
        HCHBP: this.client.clientProfile.Spouses[0].HCHBP || 0,
        HCHC: this.client.clientProfile.Spouses[0].HCHC || 0,
        HCT2D: this.client.clientProfile.Spouses[0].HCT2D || 0,
        HCCD: this.client.clientProfile.Spouses[0].HCCD || 0,
        HCC: this.client.clientProfile.Spouses[0].HCC || 0,
        HCT: this.client.clientProfile.Spouses[0].HCT || 0,
      }) : "",

      includeSpouse: this.includeSpouse,

      

      selfGender: this.client.client.GenderID,
      spouseGender: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].gender || 1 : 1,
      // Finish validating fields below this --------

      BendPointInflation: 2, // make this part of ssData ?
      SelfCurrentAge: this.calculateAge(this.client.client.DOB),
      SpouseCurrentAge: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.calculateAge(this.client.clientProfile.Spouses[0].dob) || 30 : 30,
      IsPresentValue: this.presentValue == "True" ? true : false,
      SpouseCurrentMonthlyBenefit: this.spouseClaimStatus == "True" ? this.spouseMonthlyBenefitAmount.toString().replace(/,/g,"").replace("$","") || 0 : 0,//this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? 0 : 0,
      FrmIncome: "Ind1", // make this part of ssData output
      SelfPensionStartMonth: this.client.clientProfile.PensionMonths, // ??
      isSelfSuspendable: "False", // ??
      SpouseBenefitAge: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.spouseRetirementYears : 0,
      selfCanRestrict: "False", // ??
      SpousePIA: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.spousePIA.toString().replace(/,/g,"").replace("$","") : 0,
      SelfLifeExpectancy: this.client.clientProfile.LifeExpectancy || 0, //87, // make this part of ssData output
      SpouseLifeExpectancy: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].LifeExpectancy || 0 : 0,
      StrategyDropDown: "AutoSelect",
      SelfPensionFrequency: "Monthly",
      SelfPensionStartYear: this.client.clientProfile.PensionYears, // ??
      SelfPIA: this.clientPIA,
      COLA: this.colas,
      FrmColaXml:  this.colas,
      SpousePensionStartMonth: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].pensionMonth : "01",
      SpouseGovPensionAmount: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse && this.client.clientProfile.Spouses[0].pension ? this.client.clientProfile.Spouses[0].pension.toString().replace(/,/g,"").replace("$","") || 0 : 0,
      SpouseGovPension: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].includePension : "False",
      PIAIncomeYears1: 0, // ??
      SelfAlreadyClaimedAge: 0, // ??
      spouseCanRestrict: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? "False" : "",
      SelfSubstantialEarningsYears: this.client.clientProfile.SubstantialEarningYears || 30, // ??
      SelfBenefitAge: this.clientRetirementYears,// 67,
      FrmName1: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].firstName + " " + this.client.clientProfile.Spouses[0].lastName : "",
      SpouseAlreadyClaimedAge: 0, // ??
      MaritalState: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].maritalStatus : "Single",
      SpousePensionStartYear: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].pensionYear : "",
      SpousePensionFrequency: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? "Monthly" : "",
      RetirementStrategy: "FileForBenefits", // get in ssData ??
      PIAIncomeYears2: 0, // ??
      SpouseSubstantialEarningsYears: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse && this.client.clientProfile.Spouses[0].includePension ? Number(this.client.clientProfile.Spouses[0].substantialEarningYears) : 0,
      SelfGovPension: this.client.clientProfile.IncludePension == 1 ? true : false,//"False",
      isSpouseSuspendable: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? "False" : "",

      selfRetirementYears: this.clientRetirementYears,
      selfRetirementMonths: this.clientRetirementMonths,
      spouseRetirementYears: this.spouseRetirementYears,
      spouseRetirementMonths: this.spouseRetirementMonths,

      clientSurvivorSwitchYears: this.clientSurvivorSwitchYears,
      clientSurvivorSwitchMonths: this.clientSurvivorSwitchMonths,
      SurvivorSwitchYears: this.clientSurvivorSwitchYears,
      SurvivorSwitchMonths: this.clientSurvivorSwitchMonths,

      SelfDOB: this.client.client.DOB,
      SpouseDOB: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? (this.client.clientProfile.Spouses[0].dobStr && Date.parse(this.client.clientProfile.Spouses[0].dobStr) ? this.client.clientProfile.Spouses[0].dobStr : Date.parse(this.client.clientProfile.Spouses[0].dob) ? this.client.clientProfile.Spouses[0].dob : '1/1/1960') : '1/1/1960',
      SelfCurrentIncome: this.client.clientProfile.CurrentIncome,
      SpouseCurrentIncome: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 ? this.client.clientProfile.Spouses[0].currentIncome : 0,
      SelfIRR: this.client.clientProfile.IRR,
      
      FrmDiscountRate: parseFloat((this.discountRate / 100).toString()),
      SelfRetirementAge: this.client.clientProfile.RetirementAge,
      SpouseRetirementAge: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 ? this.client.clientProfile.Spouses[0].retirementAge : 0,
      SpousePreviousSpouses: JSON.stringify(this.previousSpouse)
    }

    if (ssObject.MaritalState == 'Divorced') {
     
      ssObject.SpouseBenefitAge = 62;
      ssObject.spouseRetirementYears = 62;
      ssObject.spouseRetirementMonths = 0;
    }

    var user = {
      UserID: Number(localStorage.getItem('roleId')) <= 2 ? localStorage.getItem('selectedAdvisorID') : localStorage.getItem('userId'),
      CompanyCode: Number(localStorage.getItem('roleId')) <= 2 ? localStorage.getItem('selectedAdvisorOrgID') : localStorage.getItem('orgID'),
      ClientID: localStorage.getItem('selectedClient')
    }
    
    this.switchSurvivorMessage = this.switchSurvivorMessage.replace(" record. At age " + this.previousSurvivorYears.toString(), " record. At age " + this.clientSurvivorSwitchYears.toString());
    if (this.clientRetirementMonths == 0) {
      this.switchSurvivorMessage = this.switchSurvivorMessage.replace("At Age " + this.previousClaimYears.toString() + ", you will begin collecting", "At Age " + this.clientRetirementYears.toString() + ", you will begin collecting");
    } else {
      this.switchSurvivorMessage = this.switchSurvivorMessage.replace("At Age " + this.previousClaimYears.toString() + " and " + this.clientRetirementMonths.toString() + ", you will begin collecting", "At Age " + this.clientRetirementYears.toString() + " and " + this.clientRetirementMonths.toString() + ", you will begin collecting");
    }
    
    this.globalService.getSSData(ssObject, user)
        .subscribe(
            data => {
              this.ssData = data;
              
              this.ssData.gridData = JSON.parse(this.ssData.gridData);
              this.ssData.gridData = this.formatGridData(this.ssData.gridData);
              this.setSelectedLifeExpectancyPoint();
              
              this.comparisonGraph = this.createComparisonGraphData(data.costsByCareAndRegionBody);

              var eIllTotal = {
                clientAmountTotal: 0,
                dependentChildrenAmount: 0,
                netOfMedicareAmount: 0,
                spouseAmount: 0,
                totalAnnualAmount: 0,
              };

              var aIllTotal = {
                clientAmountTotal: 0,
                dependentChildrenAmount: 0,
                netOfMedicareAmount: 0,
                spouseAmount: 0,
                totalAnnualAmount: 0,
              };

              var opIllTotal = {
                clientAmountTotal: 0,
                dependentChildrenAmount: 0,
                netOfMedicareAmount: 0,
                spouseAmount: 0,
                totalAnnualAmount: 0,
              };

              for (var key in data.earliestIllustrationTable) {
                eIllTotal.clientAmountTotal = eIllTotal.clientAmountTotal + data.earliestIllustrationTable[key].clientAmount;
                eIllTotal.dependentChildrenAmount = eIllTotal.dependentChildrenAmount + data.earliestIllustrationTable[key].dependentChildrenAmount;
                eIllTotal.netOfMedicareAmount = Number(this.ssData.earlTotalBenefit.replace(/,/g,"").replace("$","")) - Number(this.ssData.earlPartB.replace(/,/g,"").replace("$","")); //eIllTotal.netOfMedicareAmount + data.earliestIllustrationTable[key].netOfMedicareAmount;
                eIllTotal.spouseAmount = eIllTotal.spouseAmount + data.earliestIllustrationTable[key].spouseAmount;
                eIllTotal.totalAnnualAmount = eIllTotal.totalAnnualAmount + data.earliestIllustrationTable[key].totalAnnualAmount;
                for (var keyy in data.earliestIllustrationTable[key]) {
                  if (typeof data.earliestIllustrationTable[key][keyy] === 'number') {
                    data.earliestIllustrationTable[key][keyy] = Utils.formatToCurrency(data.earliestIllustrationTable[key][keyy].toFixed(0));
                  }
                }
              }

              data.earliestIllustrationTable.totals = eIllTotal;


              for (var key in data.alternateIllustrationTable) {
                aIllTotal.clientAmountTotal = aIllTotal.clientAmountTotal + data.alternateIllustrationTable[key].clientAmount;
                aIllTotal.dependentChildrenAmount = aIllTotal.dependentChildrenAmount + data.alternateIllustrationTable[key].dependentChildrenAmount;
                aIllTotal.netOfMedicareAmount = Number(this.ssData.cusTotalBenefit.replace(/,/g,"").replace("$","")) - Number(this.ssData.cusPartB.replace(/,/g,"").replace("$","")); //aIllTotal.netOfMedicareAmount + data.alternateIllustrationTable[key].netOfMedicareAmount;
                aIllTotal.spouseAmount = aIllTotal.spouseAmount + data.alternateIllustrationTable[key].spouseAmount;
                aIllTotal.totalAnnualAmount = aIllTotal.totalAnnualAmount + data.alternateIllustrationTable[key].totalAnnualAmount;
                for (var keyy in data.alternateIllustrationTable[key]) {
                  if (typeof data.alternateIllustrationTable[key][keyy] === 'number') {
                    data.alternateIllustrationTable[key][keyy] = Utils.formatToCurrency(data.alternateIllustrationTable[key][keyy].toFixed(0));
                  }
                }
              }

              data.alternateIllustrationTable.totals = aIllTotal;

              for (var key in data.optimalIllustrationTable) {
                opIllTotal.clientAmountTotal = opIllTotal.clientAmountTotal + data.optimalIllustrationTable[key].clientAmount;
                opIllTotal.dependentChildrenAmount = opIllTotal.dependentChildrenAmount + data.optimalIllustrationTable[key].dependentChildrenAmount;
                opIllTotal.netOfMedicareAmount = Number(this.ssData.opTotalBenefit.replace(/,/g,"").replace("$","")) - Number(this.ssData.optPartB.replace(/,/g,"").replace("$",""));//opIllTotal.netOfMedicareAmount + data.optimalIllustrationTable[key].netOfMedicareAmount;
                opIllTotal.spouseAmount = opIllTotal.spouseAmount + data.optimalIllustrationTable[key].spouseAmount;
                opIllTotal.totalAnnualAmount = opIllTotal.totalAnnualAmount + data.optimalIllustrationTable[key].totalAnnualAmount;
                for (var keyy in data.optimalIllustrationTable[key]) {
                  if (typeof data.optimalIllustrationTable[key][keyy] === 'number') {
                    data.optimalIllustrationTable[key][keyy] = Utils.formatToCurrency(data.optimalIllustrationTable[key][keyy].toFixed(0));
                  }
                }
              }

              data.optimalIllustrationTable.totals = opIllTotal;

              for (var key in data.earliestIllustrationTable.totals) {
                data.earliestIllustrationTable.totals[key] = Utils.formatToCurrency(data.earliestIllustrationTable.totals[key].toFixed(0));
              }

              for (var key in data.alternateIllustrationTable.totals) {
                data.alternateIllustrationTable.totals[key] = Utils.formatToCurrency(data.alternateIllustrationTable.totals[key].toFixed(0));
              }

              for (var key in data.optimalIllustrationTable.totals) {
                data.optimalIllustrationTable.totals[key] = Utils.formatToCurrency(data.optimalIllustrationTable.totals[key].toFixed(0));
              }

              // var earliest = this.sanitizer.bypassSecurityTrustHtml(data.earliestIllustrationTable);
              // var optimal = this.sanitizer.bypassSecurityTrustHtml(data.optimalIllustrationTable);
              // var alternate = this.sanitizer.bypassSecurityTrustHtml(data.alternateIllustrationTable);

              var earliest = data.earliestIllustrationTable;
              var optimal = data.optimalIllustrationTable;
              var alternate = data.alternateIllustrationTable;


              this.illustrationComparisons = [
                { name: 'Earliest vs Alternate', data: [earliest, alternate], breakEven: 0},
                { name: 'Earliest vs Optimal', data: [earliest, optimal], breakEven: 0},
                { name: 'Alternate vs Optimal', data: [alternate, optimal], breakEven: 0}
              ]

              this.illustrationComparisons[0].breakEven = this.calculateBreakEven(this.illustrationComparisons[0].data[0], this.illustrationComparisons[0].data[1]);
              this.illustrationComparisons[1].breakEven = this.calculateBreakEven(this.illustrationComparisons[1].data[0], this.illustrationComparisons[1].data[1]);
              this.illustrationComparisons[2].breakEven = this.calculateBreakEven(this.illustrationComparisons[2].data[0], this.illustrationComparisons[2].data[1]);

              this.selectedIllustration = this.illustrationComparisons[0];
              this.loadingService.isLoading(false);
            },
            error => {
              this.error = <any>error
              this.loadingService.isLoading(false);
            });
          
  }

  calculateBreakEven(illA, illB) {
    var beIndex = 0;
    var cummulativeA = 0, cummulativeB = 0;

    for (var i = 0; i < illA.length; i++) {
      cummulativeA += Number(illA[i].totalAnnualAmount.replace(/,/g,"").replace("$",""));
      cummulativeB += Number(illB[i].totalAnnualAmount.replace(/,/g,"").replace("$",""));

      if (cummulativeA < cummulativeB && !beIndex) {
        beIndex = i;
      }
    }
    
    return beIndex;
  }

  formatGridData(data) {
    var temparray = [], chunk = 31;
    var tempSpouseArray = [];

    if (data == null)
      return;

    if (this.includeSpouse && this.client.clientProfile.Spouses[0].maritalStatus == 'Married') {
      for (var i = 0, j = data.length; i < j; i += chunk) {
        temparray.push(data.slice(i, i + chunk));
      }
    } else {
      for (var i = 0; i < data.length; i++) {
        temparray.push(data[i]);
      }
    }

    if (this.includeSpouse && this.client.clientProfile.Spouses[0].maritalStatus == 'Married') {
      var tempRow;
      for (var i = 0; i < temparray.length; i++) {
        tempRow = [];

        for (var jj = 0; jj < temparray.length; jj++) {
          tempRow.push(temparray[jj][i]);
        }

        tempSpouseArray.push(tempRow);
      }
    }

    return !this.includeSpouse || (this.includeSpouse && this.client.clientProfile.Spouses[0].maritalStatus != 'Married') ? temparray : tempSpouseArray;
  }

  setSelectedLifeExpectancyPoint() {
    // for (var key in this.ssData.gridData) {
      
    // }

    

    if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == 'Married') {
      // var firstPassedSpouse = this.ssData.selfLifeExpectancy > this.ssData.spouseLifeExpectancy ? this.ssData.selfLifeExpectancy : this.ssData.spouseLifeExpectancy;
      // var secondPassedSpouse = this.ssData.selfLifeExpectancy < this.ssData.spouseLifeExpectancy ? this.ssData.selfLifeExpectancy : this.ssData.spouseLifeExpectancy;
      // var counter1 = 0, counter2 = 0;
      // var valSet = false;

      var clientFirst = this.ssData.selfLifeExpectancy < this.ssData.spouseLifeExpectancy ? true : false;
      var valSet = false;

      for (var key in this.ssData.gridData) {
        for (var keyy in this.ssData.gridData[key]) {
          if (this.ssData.gridData[key][keyy].description.includes(this.ssData.spouseLifeExpectancy)) {
            var desc = this.ssData.gridData[key][keyy].description;
            desc = desc.split('<br />');
            
  
            if (!valSet && desc[0].includes(this.ssData.selfLifeExpectancy) && desc[1].includes(this.ssData.spouseLifeExpectancy)) {
              this.ssData.gridData[key][keyy].outline = true;
              valSet = true;
            } else if (!valSet && clientFirst && desc[0].includes(this.ssData.selfLifeExpectancy) && desc[1].includes(this.ssData.spouseLifeExpectancy)) {
              this.ssData.gridData[key][keyy].outline = true;
              valSet = true;
            } else if (!valSet && !clientFirst && desc[0].includes(this.ssData.selfLifeExpectancy) && desc[1].includes(this.ssData.spouseLifeExpectancy)) {
              this.ssData.gridData[key][keyy].outline = true;
              valSet = true;
            }
          }
        }
      }
    } else {
      for (var key in this.ssData.gridData) {
        if (this.ssData.gridData[key].description.includes(this.ssData.selfLifeExpectancy)) {
          this.ssData.gridData[key].outline = true;
          
        }
          
      }
    }
  }

  public selectedGridValues: any = {};

  gridHovered(e, cell) {
    e.target.children[0].classList.remove('grid-hover-container-hidden');
    e.target.children[0].classList.add('grid-hover-container-visible');
    cell.description = cell.description.replace('<br />','~');

    cell.firstLine = cell.description.split('~')[0];
    cell.secondLine = cell.description.split('~')[1];

    this.selectedGridValues = cell;
  }

  gridBlur(e, cell) {
    e.target.children[0].classList.remove('grid-hover-container-visible');
    e.target.children[0].classList.add('grid-hover-container-hidden');
    this.selectedGridValues = {};
  }

  handleSavingSS() {

    // report.AdvisorID = advisorID;
    // report.CompanyCode = organizationID;

    var Report = {
      ClientID: this.client.client.ID,
      AdvisorID: this.client.client.AdvisorID,
      CompanyCode: this.client.client.OrganizationID,
      ReportTypeID: 2,
    }

    var tempChildren = [];
    var tempChild = {};

    if (this.client.clientProfile.Children && this.client.clientProfile.Children.length) {
      for (var k in this.client.clientProfile.Children) {
        tempChild = {};
        tempChild["CurrentAge"] = Number(this.client.clientProfile.Children[k].DependentChildAge);//this.getNumber(this.editClientModel.clientProfile.Spouses[k].currentIncome);
        tempChild['isDisabled'] = false;
        tempChild['IsStudentAt19'] = false; //this.client.clientProfile.Children[k].DependentChildStudent == "Yes" ? true : false;
        tempChildren.push(tempChild);
      }
    }
    
    var ReportSS = {
      FrmName0: this.client.client.FirstName + " " + this.client.client.LastName,
      SelfCurrentMonthlyBenefit: this.clientMonthlyBenefitAmount,
      SpouseAlreadyCollecting: this.spouseClaimStatus, //'False',//this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.spouseClaimStatus : "False",
      SelfGovPensionAmount: this.client.clientProfile.Pension,
      Children: this.client.clientProfile.Children && this.client.clientProfile.Children.length > 0 ? JSON.stringify(tempChildren) : JSON.stringify([]),
      // Children: this.client.clientProfile.Children && this.client.clientProfile.Children.length > 0 ? JSON.stringify({Children: [
      //   {
      //     dependentChildren: this.client.clientProfile.Spouses && this.client.clientProfile.Children.length > 0 ? "True" : "False",
      //     dependentChildAge: this.client.clientProfile.Spouses && this.client.clientProfile.Children.length > 0 ? this.client.clientProfile.Children[0].DependentChildAge : 0,
      //     dependentChildDisabled: this.client.clientProfile.Spouses && this.client.clientProfile.Children.length > 0 ? "No" : "",
      //     dependentChildStudent: this.client.clientProfile.Spouses && this.client.clientProfile.Children.length > 0 ? "No" : "",
      //   }
      // ]}) : JSON.stringify({ Children: []}),
      SelfAlreadyCollecting: this.clientClaimStatus, //'False',//this.clientClaimStatus,
      //SpouseLifeExpectancy: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.ssData.spouseLifeExpectancy : 0, // make this part of ssData output
      SpouseLifeExpectancy: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].LifeExpectancy || 0 : 0,


      // Finish validating fields below this --------

      BendPointInflation: 2, // make this part of ssData ?
      SelfCurrentAge: this.calculateAge(this.client.client.DOB),
      SpouseCurrentAge: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.calculateAge(this.client.clientProfile.Spouses[0].dob) || 30 : 30,
      IsPresentValue: this.presentValue == "True" ? 1 : 0,
      SpouseCurrentMonthlyBenefit: this.spouseClaimStatus == "True" ? this.spouseMonthlyBenefitAmount.toString().replace(/,/g,"").replace("$","") || 0 : 0,//this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? 0 : 0,
      FrmIncome: this.ssData.frmIncome,//"Ind1", // make this part of ssData output
      SelfPensionStartMonth: this.client.clientProfile.PensionMonths, // ??
      isSelfSuspendable: "False", // ??
      SpouseBenefitAge: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.spouseRetirementYears : 0,
      selfCanRestrict: "False", // ??
      SpousePIA: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.spousePIA.toString().replace(/,/g,"").replace("$","") : 0,
      SelfLifeExpectancy: this.ssData.selfLifeExpectancy, // make this part of ssData output
      StrategyDropDown: "AutoSelect",
      SelfPensionFrequency: "Monthly",
      SelfPensionStartYear: this.client.clientProfile.PensionYears, // ??
      SelfPIA: this.clientPIA,
      COLA: JSON.stringify({ Colas: this.colas }),
      FrmColaXml:  JSON.stringify({ Colas: this.colas }),
      SpousePensionStartMonth: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].pensionMonth : "01",
      SpouseGovPensionAmount: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse && this.client.clientProfile.Spouses[0].pension ? this.client.clientProfile.Spouses[0].pension.toString().replace(/,/g,"").replace("$","") : "",
      SpouseGovPension: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].includePension : "False",
      PIAIncomeYears1: 0, // ??
      SelfAlreadyClaimedAge: 0, // ??
      spouseCanRestrict: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? "False" : "",
      SelfSubstantialEarningsYears: this.client.clientProfile.SubstantialEarningYears || 30,//30, // ??
      SelfBenefitAge: this.clientRetirementYears,// 67,
      FrmName1: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].firstName + " " + this.client.clientProfile.Spouses[0].lastName : "",
      SpouseAlreadyClaimedAge: 0, // ??
      MaritalState: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].maritalStatus : "Single",
      SpousePensionStartYear: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? this.client.clientProfile.Spouses[0].pensionYear : "",
      SpousePensionFrequency: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? "Monthly" : "",
      RetirementStrategy: "FileForBenefits", // get in ssData ??
      PIAIncomeYears2: 0, // ??
      SpouseSubstantialEarningsYears: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse && this.client.clientProfile.Spouses[0].includePension ? Number(this.client.clientProfile.Spouses[0].substantialEarningYears) : 0,
      SelfGovPension: this.client.clientProfile.IncludePension == 1 ? true : false,
      isSpouseSuspendable: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? "False" : "",
      SelfDOB: this.client.client.DOB,
      SpouseDOB: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? (this.client.clientProfile.Spouses[0].dobStr && Date.parse(this.client.clientProfile.Spouses[0].dobStr) ? this.client.clientProfile.Spouses[0].dobStr : Date.parse(this.client.clientProfile.Spouses[0].dob) ? this.client.clientProfile.Spouses[0].dob : '1/1/1960') : '1/1/1960',
      FrmState: this.client.clientProfile.RetirementState,
      FrmState1: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 ? this.client.clientProfile.Spouses[0].state : '',
      selfRetirementYears: this.clientRetirementYears,
      selfRetirementMonths: this.clientRetirementMonths,
      spouseRetirementYears: this.spouseRetirementYears,
      spouseRetirementMonths: this.spouseRetirementMonths,

      SelfCurrentIncome: this.client.clientProfile.CurrentIncome,
      SpouseCurrentIncome: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 ? this.client.clientProfile.Spouses[0].currentIncome : 0,
      SelfIRR: this.client.clientProfile.IRR,

      clientSurvivorSwitchYears: this.clientSurvivorSwitchYears,
      clientSurvivorSwitchMonths: this.clientSurvivorSwitchMonths,
      SurvivorSwitchYears: this.clientSurvivorSwitchYears,
      SurvivorSwitchMonths: this.clientSurvivorSwitchMonths,

      selfConditions: JSON.stringify({
        HCHBP: this.client.clientProfile.HCHBP,
        HCHC: this.client.clientProfile.HCHC,
        HCT2D: this.client.clientProfile.HCT2D,
        HCCD: this.client.clientProfile.HCCD,
        HCC: this.client.clientProfile.HCC,
        HCT: this.client.clientProfile.HCT,
      }),
      spouseConditions: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 && this.includeSpouse ? JSON.stringify({
        HCHBP: this.client.clientProfile.Spouses[0].HCHBP || 0,
        HCHC: this.client.clientProfile.Spouses[0].HCHC || 0,
        HCT2D: this.client.clientProfile.Spouses[0].HCT2D || 0,
        HCCD: this.client.clientProfile.Spouses[0].HCCD || 0,
        HCC: this.client.clientProfile.Spouses[0].HCC || 0,
        HCT: this.client.clientProfile.Spouses[0].HCT || 0,
      }) : "",

      FrmDiscountRate: parseFloat((this.discountRate / 100).toString()),
      AddedSocialBenefit: Number(this.ssData.cusTotalBenefit.replace(/,/g,"").replace("$","")) - Number(this.ssData.earlTotalBenefit.replace(/,/g,"").replace("$","")),
      SelfRetirementAge: this.client.clientProfile.RetirementAge,
      SpouseRetirementAge: this.client.clientProfile.Spouses && this.client.clientProfile.Spouses.length > 0 ? this.client.clientProfile.Spouses[0].retirementAge : 0,
      SpousePreviousSpouses: JSON.stringify(this.previousSpouse)
      // includeSpouse: this.includeSpouse,
    }
    
    if (ReportSS.MaritalState == 'Divorced') {
      ReportSS.SpouseBenefitAge = 62;
      ReportSS.spouseRetirementYears = 62;
      ReportSS.spouseRetirementMonths = 0;
    }
    //console.log(ReportSS);
    this.globalService.createReportSS(Report, ReportSS)
    .subscribe(
        resp => {
          if (resp) {
            this.snackBarService.add("Please wait while your social security report is being generated.", 0);
            //this.router.navigateByUrl('/reports/create?d=t');
            this.downloadReport(resp.ReportID, resp.AdvisorID, resp.ClientID, resp.CompanyCode, resp.ReportTypeID);
          }
        },
        error => this.error = <any>error);
  }

  downloadReport(reportID, advisorID, clientID, orgID, reportTypeID) {
    this.loadingService.isLoading(true);

    var ReportFields = { reportID, advisorID, clientID, orgID, ReportTypeID: reportTypeID, token: localStorage.getItem("bearer") };

    this.globalService.generateReport(ReportFields, this.client.client, ReportFields)
      .subscribe(
      resp => {
        this.loadingService.isLoading(false);
      },
      error => {
        this.error = <any>error;
        this.loadingService.isLoading(false);
      });
  }

  getClassColor(isComparison) {
    

    var className = ""

    if (this.selectedIllustration.name === 'Earliest vs Alternate') {
      className = isComparison ? 'earliest' : 'alternate';
    } else if (this.selectedIllustration.name === 'Earliest vs Optimal') {
      className = isComparison ? 'earliest' : 'optimal';
    } else if (this.selectedIllustration.name === 'Alternate vs Optimal') {
      className = isComparison ? 'alternate' : 'optimal';
    }

    return className
  }

  createComparisonGraphData(data) {
    var result = [], item;

    data = data.replace(/<tr>/g, '');
    data = data.replace(/<\/tr>/g, '%END%');
    data = data.split('%END%');


    for (var i = 0; i < this.illustrationsTypes.length; i++) {
      item = {
        name: this.illustrationsTypes[i],
        series: []
      }
      
      for (var j = 0; j < data.length; j++) {
        var dataItem = data[j].replace(/<th>/g, '');
        dataItem = dataItem.replace(/<\/th>/g, '%END%');
        dataItem = dataItem.replace('Ages', '').split('%END%');
  
        if (dataItem[1] && dataItem[0]) {
          var ages = dataItem[0];
          var illustrations = dataItem[1].replace(/<td>/g, '');
          illustrations = illustrations.replace(/<\/td>/g, '%END%');
          illustrations = illustrations.split('%END%');

          item.series.push({
            name: ages,
            value: Number(illustrations[i]).toFixed(0) || 0
          })
          
          // item = {
          //   name: illustrationsTypes[i],
          //   series: [
          //     { name: ages, value: Number(illustrations[0]) || 0},
          //     { name: "Alternate", value: Number(illustrations[1]) || 0 },
          //     { name: "Optimal", value: Number(illustrations[2]) || 0 }
          //   ]
          // }
  
        }
        
      }
      result.push(item);
    }


    
    Object.assign(this, { val: result});
    return {};
  }

  includePartner(e) {
    this.includeSpouse = e.target.checked;
    this.getSSData();
  }

  // setSSChartData(data: any) {
  //   var brackets = [];
  //   this.areaChartData = [];

  //   for (var i = 1; i <= 5; i++) {
  //     brackets.push(data.IllustrationEntries.filter((entry) => entry.IncomeBracket === i));
  //   }

  //   brackets.map((bracketEntries, i) => {
  //     if (bracketEntries.length > 0) {
  //       this.areaChartData.push({
  //         name: `Bracket ${i + 1} Premium`,
  //         series: bracketEntries.map((entry) => { return { name: entry.Year.toString(), value: entry.HouseholdExpenseByBracket[0]}})
  //       });
        
  //       if (i !== 0) { // if it's the first bracket -- there are no surcharges
  //         this.areaChartData.push({
  //           name: `Bracket ${i + 1} Surcharge`,
  //           series: bracketEntries.map((entry) => { return { name: entry.Year.toString(), value: entry.HouseholdExpenseByBracket[1] - entry.HouseholdExpenseByBracket[0]}})
  //         });
  //       }
  //     }
  //   });

  

  //   // Remove empty series
  //   for (var i = 0; i < this.areaChartData.length; i++) {
  //     this.areaChartData[i].series = this.areaChartData[i].series.filter(item => item.value > 0 && item.name >= (data.Client.RetirementAge - data.Client.CurrentAge) + (new Date()).getFullYear());
  //   }

  

  //   this.areaChartData = this.areaChartData.filter((data) => { return data.series.length > 0 });
    
  //   Object.assign(this, { val: this.areaChartData});
  //   this.chartDataLoaded = true;
  // }

  roundNumber(num) {
    return Math.round(num);
  }

  public dependentChildCount: number = 0;
  //outLiveSpouse = false;
  getClient() {
    this.globalService.getClientData(Number(localStorage.getItem("selectedClient")), 4)
    .subscribe(
        client => {
          //console.log(client);
          this.client = client;
          this.client.clientProfile.Spouses = JSON.parse(this.client.clientProfile.Spouses);

          if (this.client.clientProfile.Spouses.length > 1 && this.client.clientProfile.Spouses[0].maritalStatus != 'Single') {
            this.client.clientProfile.Spouses = Utils.calculateMostProfitableSpouse(this.client, this.client.clientProfile.Spouses);
          }

          if (this.client.clientProfile.Spouses[0]) {
            if (this.client.clientProfile.Spouses[0].maritalStatus == 'Divorced' && !this.client.clientProfile.Spouses[0].alive) {
              this.client.clientProfile.Spouses[0].maritalStatus = 'Widowed';
              this.client.clientProfile.Spouses = Utils.calculateMostProfitableSpouse(this.client, this.client.clientProfile.Spouses);
            } else if (this.client.clientProfile.Spouses[0].maritalStatus == 'Divorced' && this.client.clientProfile.Spouses[0].alive && !this.client.clientProfile.Spouses[0].tenyears) {
              this.client.clientProfile.Spouses = [];
            }
          }
          
          if (this.client.clientProfile.Spouses.length) {
            // this.spouseRetirementYears = this.client.clientProfile.Spouses[0].retirementAge;
            this.spousePIA = typeof this.client.clientProfile.Spouses[0].pia === 'string' ? this.client.clientProfile.Spouses[0].pia.replace(/,/g,"").replace("$","") : this.client.clientProfile.Spouses[0].pia;
          }
          
          this.client.clientProfile.Children = JSON.parse(this.client.clientProfile.Children);
          this.includeSpouse = Boolean(this.client.clientProfile.Spouses.length);
          
          if (this.client.clientProfile.Spouses[0] && this.client.clientProfile.Spouses[0].maritalStatus === 'Single') {
            this.includeSpouse = false;
            this.client.clientProfile.Spouses = [];
          }


          

          this.clientPIA = this.client.clientProfile.PIA;

          let FRA = this.getFRA(this.client.client.DOB);

          this.clientRetirementYears = FRA.age;
          this.clientRetirementMonths = FRA.month;

          this.clientClaimStatus = this.calculateAge(this.client.client.DOB) >= 70 ? "True" : "False";

          this.clientClaimStatusDisabled = this.calculateAge(this.client.client.DOB) < 62 && !this.client.clientProfile.Spouses ||
                                           this.calculateAge(this.client.client.DOB) < 62 && this.client.clientProfile.Spouses[0] && this.client.clientProfile.Spouses[0].maritalStatus !== 'Widowed' || 
                                           this.calculateAge(this.client.client.DOB) < 60 && this.client.clientProfile.Spouses[0] && this.client.clientProfile.Spouses[0].maritalStatus === 'Widowed' ||
                                           this.calculateAge(this.client.client.DOB) >= 70;

          var clientMonths = 0;
          var spouseMonths = 0;

          // Past FRA, past retirement
          if (this.clientRetirementYears <= this.calculateAge(this.client.client.DOB)) {
            this.clientRetirementYears = this.calculateAge(this.client.client.DOB);

            clientMonths = (new Date().getMonth() + 1) - this.getBirthdayMonth(this.client.client.DOB);

            if (clientMonths > 0) {
              this.clientRetirementMonths = clientMonths;
            } else if (clientMonths < 0) {
              this.clientRetirementMonths = 12 + clientMonths;
            } else {
              this.clientRetirementMonths = 0;
            }
          }

          

          // if (this.clientRetirementYears > 70 || this.clientRetirementYears == 70 && this.clientRetirementYears > 0) {
          //   this.clientClaimStatus = 'True';
          // }

          //this.clientRetirementYears = this.calculateAge(this.client.client.DOB) === 70 ? this.calculateAge(this.client.client.DOB) : 67;

          this.dependentChildCount = this.client.clientProfile.Children && this.client.clientProfile.Children.length || 0;


          let previousSpouseWidowSpouse;
          
          for (let spousesSpouse in this.client.clientProfile.Spouses) {
            for (let previousSpouse in this.client.clientProfile.Spouses[spousesSpouse].spousesSpouse) {
              if (this.client.clientProfile.Spouses[spousesSpouse].spousesSpouse[previousSpouse].maritalStatus == 'Widowed')
                previousSpouseWidowSpouse = this.client.clientProfile.Spouses[spousesSpouse].spousesSpouse[previousSpouse];
            }
          }

          this.previousSpouse = previousSpouseWidowSpouse || { pia: "$0"};
          
        /* Internal notes:
        in client make married couple divorced
        change spouse primary insurance amount to be 1500 (make sure this number is twice as mucch then the below)
        change client primary insurance to be 500
        client has to outlive spouse by a few years
        need to be able to set spouse life expectancy
        -run ss report and set all COLA to be 0 for ease of looking

        when the ex-spouse is alive the client gets half; when the ex-spouse is dead, the client gets it all 
        (focus on Alernate cases)
        */
          if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus != 'Married') {
            if (this.client.clientProfile.Spouses[0].maritalStatus == 'Divorced')
              //this.spouseRetirementYears = this.calculateAge(this.client.clientProfile.Spouses[0].dob) > 67 ? Number(this.calculateAge(this.client.clientProfile.Spouses[0].dob)) : 67;
              this.spouseRetirementYears = this.calculateAge(this.client.clientProfile.Spouses[0].dob) >= 67 ? Number(this.calculateAge(this.client.clientProfile.Spouses[0].dob) + 1) : 67;
            else {
              //this.spouseRetirementYears = this.calculateAge(this.client.clientProfile.Spouses[0].dob) > 67 ? Number(this.calculateAge(this.client.clientProfile.Spouses[0].dob)) : 67;
              this.spouseRetirementYears = this.calculateAge(this.client.clientProfile.Spouses[0].dob) >= 67 ? Number(this.calculateAge(this.client.clientProfile.Spouses[0].dob) + 1) : 67;
            }
            
            if (this.client.clientProfile.Spouses[0].maritalStatus == 'Divorced' && this.client.clientProfile.Spouses[0].alive == 'false') {
              this.client.clientProfile.Spouses[0].maritalStatus = 'Widowed'
            }

            this.spouseRetirementMonths = 1;
          }

          if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == 'Married' || this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == 'Divorced') {
            this.spouseClaimStatus = this.calculateAge(this.client.clientProfile.Spouses[0].dob) >= 70 ? "True" : "False";
            this.spouseClaimStatusDisabled = this.calculateAge(this.client.clientProfile.Spouses[0].dob) < 62 || this.calculateAge(this.client.clientProfile.Spouses[0].dob) >= 70;

            let FRA = this.getFRA(this.client.clientProfile.Spouses[0].dob);

            this.spouseRetirementYears = FRA.age;
            this.spouseRetirementMonths = FRA.month;

            // Past FRA, past retirement
            if (this.spouseRetirementYears <= this.calculateAge(this.client.clientProfile.Spouses[0].dob)) {
              this.spouseRetirementYears = this.calculateAge(this.client.clientProfile.Spouses[0].dob);
              this.spouseRetirementMonths = this.getBirthdayMonth(this.client.clientProfile.Spouses[0].dob);
              spouseMonths = (new Date().getMonth() + 1) - this.getBirthdayMonth(this.client.clientProfile.Spouses[0].dob);

              if (spouseMonths > 0) {
                this.spouseRetirementMonths = spouseMonths;
              } else if (spouseMonths < 0) {
                this.spouseRetirementMonths = 12 + spouseMonths;
              } else {
                this.spouseRetirementMonths = 0;
              }
            }
            
            // if (this.spouseRetirementYears > 70 || this.spouseRetirementYears == 70 && this.spouseRetirementMonths > 0) {
            //   this.spouseClaimStatus = 'True';
            // }


            // this.spouseRetirementYears = this.calculateAge(this.client.clientProfile.Spouses[0].dob) >= 67 ? Number(this.calculateAge(this.client.clientProfile.Spouses[0].dob) + 1) : 67;
            this.spouseMonthlyBenefitAmount = this.client.clientProfile.Spouses[0].pia;

            this.spouseStartPIA = (70/100) * this.spouseMonthlyBenefitAmount;
            this.spouseEndPIA = (132/100) * this.spouseMonthlyBenefitAmount;  
           
          }

          // if (this.client.clientProfile.Spouses.length && this.client.clientProfile.Spouses[0].maritalStatus == 'Widowed') {
          //   if (this.calculateAge(this.client.client.DOB) < 60) {
          //     this.clientRetirementYears = 60;
          //     this.clientRetirementMonths = 0;
          //   } else if (this.calculateAge(this.client.client.DOB) >= 70) {
          //     this.clientRetirementYears = 69;
          //     this.clientRetirementMonths = 11;
          //   } else {
          //     this.clientRetirementYears = this.calculateAge(this.client.client.DOB);
          //     this.clientRetirementMonths = 0;
          //   }
          // }

          this.clientMonthlyBenefitAmount = this.client.clientProfile.PIA;

          this.startPIA = (70/100) * this.clientMonthlyBenefitAmount;
          this.endPIA = (132/100) * this.clientMonthlyBenefitAmount;

          // if (this.client.clientProfile.Spouses[0].maritalStatus == 'Widow') {
          //   this.includeSpouse = false;
          // }
          //this.outLiveSpouse = (parseInt(this.client.clientProfile.LifeExpectancy) > parseInt(this.client.clientProfile.Spouses[0].LifeExpectancy));

          this.getSSData();
        },
        error => this.error = <any>error);
  }

  calculateAge(birthday) {
    birthday = new Date(birthday);
    var ageDifMs = Date.now() - birthday;
    var ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  expandIllustrationDD() {
    if (this.illustrationDD.nativeElement.classList.contains('illustrationDDHidden')) {
      this.illustrationDD.nativeElement.classList.remove('illustrationDDHidden');
      this.illustrationDD.nativeElement.classList.add('illustrationDDVisible');
    } else {
      this.illustrationDD.nativeElement.classList.remove('illustrationDDVisible');
      this.illustrationDD.nativeElement.classList.add('illustrationDDHidden');
    }
  }

  updateCashFlowChart(illustration) {
    this.selectedIllustration = illustration;
  }

  sumBenefit(benefitTotal, medicareDeduction) {
    if (benefitTotal && medicareDeduction) {
      return '$' + (benefitTotal.replace(/[^0-9\.-]+/g,"") - medicareDeduction.replace(/[^0-9\.-]+/g,"")).toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1,");
    }
  }

  handleEditClientModal(e) {
    this.modalService.add('add-edit-client', { ID: this.client.client.ID });
    // this.modalSubscription.add('edit-client', { ID: this.client.client.ID });
  }

  //https://www.ssa.gov/planners/retire/retirechart.html
  getFRA(bday) {
    bday = typeof bday === 'string' ? bday : bday.formatted;
    let yearOfBirth = Number(bday.substring(bday.length - 4, bday.length));
    let FRA = {
      age: 0,
      month: 0,
    }

    if (yearOfBirth <= 1937) {
      FRA.age = 65;
      FRA.month = 0;
    } else if (yearOfBirth === 1938) {
      FRA.age = 65;
      FRA.month = 2;
    } else if (yearOfBirth === 1939) {
      FRA.age = 65;
      FRA.month = 4;
    } else if (yearOfBirth === 1940) {
      FRA.age = 65;
      FRA.month = 6;
    } else if (yearOfBirth === 1941) {
      FRA.age = 65;
      FRA.month = 8;
    } else if (yearOfBirth === 1942) {
      FRA.age = 65;
      FRA.month = 10;
    } else if (yearOfBirth >= 1943 && yearOfBirth <= 1954) {
      FRA.age = 66;
      FRA.month = 0;
    } else if (yearOfBirth === 1955) {
      FRA.age = 66;
      FRA.month = 2;
    } else if (yearOfBirth === 1956) {
      FRA.age = 66;
      FRA.month = 4;
    } else if (yearOfBirth === 1957) {
      FRA.age = 66;
      FRA.month = 6;
    } else if (yearOfBirth === 1958) {
      FRA.age = 66;
      FRA.month = 8;
    } else if (yearOfBirth === 1959) {
      FRA.age = 66;
      FRA.month = 10;
    } else if (yearOfBirth >= 1960) {
      FRA.age = 67;
      FRA.month = 0;
    } 

    return FRA;
  }

  getBirthdayMonth(date = null) {
    var d = new Date(date);
    return (d.getMonth() + 1);
  }
}
